import { t } from "../utils/i18n.ts";
import { COLORS } from "../core/constants.ts";
import { UI_FONT_STACK, UI_Z, roleFontSize } from "./uiTokens.ts";
import { ctaSystemCss, litPanelCss, rgba } from "./ctaStyles.ts";

export interface SellDialogOptions {
  title: string;
  itemName: string;
  invested: number;
  refund: number;
  refundPercent: number;
  onConfirm: () => void;
  onCancel: () => void;
}

export class SellConfirmDialog {
  private overlay: HTMLDivElement;
  private panel: HTMLDivElement;
  private visible = false;

  constructor(parent: HTMLElement) {
    SellConfirmDialog.injectStyles();

    this.overlay = document.createElement("div");
    this.overlay.className = "sell-confirm-overlay";
    Object.assign(this.overlay.style, {
      position: "absolute",
      inset: "0",
      display: "none",
      alignItems: "center",
      justifyContent: "center",
      // A faint lit backdrop wash (cool, on-brand) instead of flat black —
      // dims the board without going to a dead grey.
      background: rgba(COLORS.BG_PRIMARY, 0.78),
      // BL-198 — named z-tier (was the ad-hoc literal 950).
      zIndex: String(UI_Z.DIALOG),
    });

    // BL-187 — rebuild on the lit-panel MATERIAL (was a flat Object.assign box).
    // The panel reads as the same lit CRT hardware as the rest of the app: an
    // inner-gradient lit surface, a rounded corner, and an edge-glow stroke — all
    // from `UI_PANEL_MATERIAL`'s tokens (via litPanelCss), tinted ORACLE_RED to
    // signal a destructive action without abandoning the material language.
    this.panel = document.createElement("div");
    this.panel.className = "sell-confirm-panel";
    // BL-255 — proper modal semantics so assistive tech announces the dialog
    // and the accessible name reads the destructive intent.
    this.panel.setAttribute("role", "alertdialog");
    this.panel.setAttribute("aria-modal", "true");
    this.panel.style.cssText = litPanelCss(COLORS.ORACLE_RED);
    Object.assign(this.panel.style, {
      fontFamily: UI_FONT_STACK,
      color: COLORS.SENTINEL_GREEN,
      padding: "26px 34px",
      minWidth: "320px",
      maxWidth: "420px",
      textAlign: "center",
      // BL-184 — wrap guard on the 320-420px panel so a long item name / DE copy
      // wraps inside the box instead of overflowing (word-break:normal keeps
      // normal words breaking only at spaces).
      overflowWrap: "break-word",
      wordBreak: "normal",
    });

    this.overlay.appendChild(this.panel);
    parent.appendChild(this.overlay);

    // BL-198 dismiss contract — backdrop-click + Esc (Enter confirms the primary).
    this.overlay.addEventListener("click", (e) => {
      if (e.target === this.overlay) this.cancel();
    });

    // BL-255 dismiss contract. Escape cancels. Enter/Space are DELIBERATELY not
    // globally bound to confirm — the previous global handler fired confirm()
    // even when CANCEL was focused (and preventDefault suppressed Cancel's own
    // activation), so a keyboard user could never cancel via the Cancel button
    // and could destroy a tower by pressing Space on it. Both buttons are real
    // <button>s, so their native Enter/Space activation is correct and
    // focus-aware; we only add a Tab focus-trap between the two.
    document.addEventListener("keydown", (e) => {
      if (!this.visible) return;
      if (e.key === "Escape") {
        e.stopPropagation();
        this.cancel();
        return;
      }
      if (e.key === "Tab") {
        this.trapFocus(e);
      }
    });
  }

  /** Keep Tab focus inside the two dialog buttons while it is open (BL-255). */
  private trapFocus(e: KeyboardEvent): void {
    const focusable = Array.from(
      this.panel.querySelectorAll<HTMLButtonElement>("button"),
    );
    if (focusable.length === 0) return;
    const first = focusable[0]!;
    const last = focusable[focusable.length - 1]!;
    const active = document.activeElement;
    if (e.shiftKey && active === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && active === last) {
      e.preventDefault();
      first.focus();
    } else if (!this.panel.contains(active)) {
      e.preventDefault();
      first.focus();
    }
  }

  private currentOnConfirm: (() => void) | null = null;
  private currentOnCancel: (() => void) | null = null;
  private previouslyFocused: HTMLElement | null = null;

  show(opts: SellDialogOptions): void {
    this.currentOnConfirm = opts.onConfirm;
    this.currentOnCancel = opts.onCancel;
    this.previouslyFocused =
      document.activeElement instanceof HTMLElement
        ? document.activeElement
        : null;
    this.visible = true;

    // BL-255 — accessible name spells out the destructive action + the deal.
    this.panel.setAttribute(
      "aria-label",
      `${opts.title} — ${opts.itemName}. ${t("sell.refund")} ¢${opts.refund} (${opts.refundPercent}%). ${t("sell.pathWarning")}`,
    );

    // BL-187 — AA colours: the two old `@50%`-alpha labels (TEXT_PRIMARY80
    // 4.21:1, SENTINEL_GREEN80 3.83:1, §4a) are replaced by full-strength tokens
    // (TEXT_SECONDARY 4.66:1 on the panel, SENTINEL_GREEN 12:1) — both clear AA.
    this.panel.innerHTML = `
      <div style="font-size:${roleFontSize("heading")};text-transform:uppercase;letter-spacing:0.15em;margin-bottom:12px;color:${COLORS.ORACLE_RED};text-shadow:0 0 12px ${rgba(COLORS.ORACLE_RED, 0.5)}">
        ${opts.title}
      </div>
      <div style="font-size:${roleFontSize("label")};margin-bottom:8px;color:${COLORS.SENTINEL_GREEN}">
        ${opts.itemName}
      </div>
      <div style="font-size:${roleFontSize("caption")};margin-bottom:4px;color:${COLORS.TEXT_SECONDARY}">
        ${t("sell.invested")}: ¢${opts.invested}
      </div>
      <div style="font-size:${roleFontSize("body")};margin-bottom:12px;color:${COLORS.SENTINEL_GREEN}">
        ${t("sell.refund")}: ¢${opts.refund} (${opts.refundPercent}%)
      </div>
      <div style="font-size:${roleFontSize("micro")};margin-bottom:18px;color:${COLORS.AMBER_WARN}">
        ⚠ ${t("sell.pathWarning")}
      </div>
      <div style="display:flex;gap:16px;justify-content:center">
        <button class="sell-cta sell-cta-primary sell-confirm-btn">
          <span class="cta-foil"></span>${t("sell.confirm")}
        </button>
        <button class="sell-cta sell-cancel-btn">${t("sell.cancel")}</button>
      </div>
    `;

    this.panel
      .querySelector(".sell-confirm-btn")!
      .addEventListener("click", () => this.confirm());
    this.panel
      .querySelector(".sell-cancel-btn")!
      .addEventListener("click", () => this.cancel());

    this.overlay.style.display = "flex";

    // BL-255 — move focus to the SAFE choice (Cancel) on open, so a reflexive
    // Enter/Space cancels rather than sells.
    const cancelBtn = this.panel.querySelector<HTMLButtonElement>(
      ".sell-cancel-btn",
    );
    cancelBtn?.focus();
  }

  private confirm(): void {
    if (!this.visible) return;
    this.visible = false;
    this.overlay.style.display = "none";
    this.restoreFocus();
    this.currentOnConfirm?.();
    this.currentOnConfirm = null;
    this.currentOnCancel = null;
  }

  private cancel(): void {
    if (!this.visible) return;
    this.visible = false;
    this.overlay.style.display = "none";
    this.restoreFocus();
    this.currentOnCancel?.();
    this.currentOnConfirm = null;
    this.currentOnCancel = null;
  }

  /** BL-255 — return focus to the control that opened the dialog. */
  private restoreFocus(): void {
    this.previouslyFocused?.focus?.();
    this.previouslyFocused = null;
  }

  isVisible(): boolean {
    return this.visible;
  }

  destroy(): void {
    this.overlay.remove();
  }

  /** Inject the shared premium CTA stylesheet once. The PRIMARY (CONFIRM SELL)
   *  is tinted ORACLE_RED (destructive); the secondary (CANCEL) is the ghost
   *  lit-panel rank. The reduced-motion seam comes for free from ctaSystemCss. */
  private static injectStyles(): void {
    if (document.getElementById("sell-confirm-styles")) return;
    const style = document.createElement("style");
    style.id = "sell-confirm-styles";
    style.textContent = ctaSystemCss({
      scope: ".sell-confirm-overlay",
      btnClass: "sell-cta",
      primaryClass: "sell-cta-primary",
      primaryColour: COLORS.ORACLE_RED,
    });
    document.head.appendChild(style);
  }
}
